<template>
    <div id="hamburger-menu" class="fixed z-40 inset-0" role="menu" aria-labelledby="menubutton">
        <div
            class="absolute inset-0 bg-overlay-black-40 transition-opacity duration-500"
            :class="{
                'opacity-0': !active,
                'opacity-100': active,
            }"
            @click="closeMenu"
        />

        <div
            :data-theme="settings.mainNav_ColorTheme"
            :data-mode="settings.mainNav_ColorPalette"
            class="absolute inset-y-0 w-full max-w-[500px] lg:max-w-[800px] surface-background flex flex-col transition-transform duration-500  overflow-auto lg:overflow-hidden"
            :class="{
                'left-0': position === 'left',
                '-translate-x-full': !active && position === 'left',
                'translate-x-0': active && position === 'left',
                'left-full': position === 'right',
                'translate-x-0': !active && position === 'right',
                '!-translate-x-full': active && position === 'right',
            }"
        >
            <div
                class="flex-shrink-0 z-10 flex items-center px-6 lg:px-11 py-6 space-x-2 justify-end"
                :class="{
                    'flex-row-reverse': position === 'left',
                }"
            >
                <div
                    ref="actionButtons"
                    class="hidden lg:flex items-center space-x-2"
                    :class="{
                        'mr-auto': position === 'right',
                        'ml-auto': position === 'left',
                    }"
                >
                    <template v-if="useActionDropdown">
                        <DDropdown>
                            <template #trigger="dropdownProps">
                                <DButton
                                    theme="transparent"
                                    right-icon="caret-down"
                                    icon-set="ph"
                                    size="lg"
                                    :active="dropdownProps.active"
                                >
                                    Actions
                                </DButton>
                            </template>

                            <NuxtLink
                                v-for="(button, buttonIndex) in buttons"
                                :key="buttonIndex"
                                :to="button.url"
                                :target="button.target"
                                no-prefetch
                            >
                                <DDropdownItem>
                                    {{ button.text }}
                                </DDropdownItem>
                            </NuxtLink>
                        </DDropdown>
                    </template>

                    <template v-else>
                        <NuxtLink
                            v-for="(button, buttonIndex) in buttons"
                            :key="buttonIndex"
                            :to="button.url"
                            :target="button.target"
                            no-prefetch
                        >
                            <DButton :theme="buttonTheme(button.buttonStyle)" size="lg">
                                {{ button.text }}
                            </DButton>
                        </NuxtLink>
                    </template>
                </div>


                <!-- search & quicklinks -->
                <NuxtLink
                    v-if="showSearch"
                    to="/search"
                    :class="{
                        '!mr-auto !ml-0 lg:!mr-2': position === 'right',
                        '!ml-auto !mr-0 lg:!ml-2': position === 'left',
                    }"
                    no-prefetch
                >
                    <DButton
                        theme="transparent"
                        icon="magnifying-glass"
                        icon-set="ph"
                        size="lg"
                        @click="closeMenu"
                    />
                </NuxtLink>

                <span class="hidden lg:block w-px h-[22px] bg-[--border-base]" />

                <DDropdown
                    v-if="quicklinks?.mainNavQuicklinksItem_Links && quicklinks.mainNavQuicklinksItem_Links.length > 0"
                    class="hidden lg:inline-flex"
                >
                    <template #trigger="dropdownProps">
                        <DButton
                            theme="transparent"
                            right-icon="caret-down"
                            icon-set="ph"
                            size="lg"
                            :active="dropdownProps.active"
                        >
                            {{ quicklinks.mainNavQuicklinksItem_Heading }}
                        </DButton>
                    </template>

                    <NuxtLink
                        v-for="(quicklink, index) in quicklinks.mainNavQuicklinksItem_Links"
                        :key="index"
                        :to="quicklink.url"
                        :target="quicklink.target"
                        no-prefetch
                    >
                        <DDropdownItem
                            :left-icon="quicklink?.icon?.name"
                            :icon-set="quicklink?.icon?.set"
                            :icon-type="quicklink?.icon?.type"
                        >
                            {{quicklink.text}}
                        </DDropdownItem>
                    </NuxtLink>
                </DDropdown>

                <DButton
                    right-icon="x"
                    icon-set="ph"
                    size="lg"
                    @click="closeMenu"
                >
                    Close
                </DButton>
            </div>

            <div class="lg:flex lg:items-stretch lg:flex-1 lg:pt-11 lg:overflow-hidden">
                <div class="flex flex-col lg:space-y-1 lg:w-[380px] lg:px-6 border-t border-b border-overlay-white-10 lg:border-transparent divide-y divide-overlay-white-10 lg:divide-none overflow-y-auto">
                    <div
                        v-for="(entry, entryIndex) in entries"
                        :key="entryIndex"
                        class="text-neutral-strong text-3xl leading-3xl font-semibold px-6 py-5 lg:px-4 lg:py-3 cursor-pointer transition-all duration-150 radius-card-sm hover:bg-overlay-black-10 lg:hover:!opacity-100"
                        :class="{
                            'lg:opacity-50': activeEntry && activeEntry !== entry,
                            'bg-overlay-black-10': activeEntry === entry,
                        }"
                        @mouseenter="onMouseEnter($event, entry)"
                    >
                        <div class="flex items-center">
                            <NuxtLink
                                class="pr-2 hover:text-neutral-extra-strong hover:underline"
                                :to="`/${entry.uri}`"
                                :target="entry.redirectLink?.[0].target ?? ''"
                                @click.stop.prevent
                            >
                                {{entry.title}}
                            </NuxtLink>

                            <DButton
                                v-if="entry.children.length > 0"
                                theme="transparent"
                                icon="caret-right"
                                icon-set="ph"
                                class="pointer-events-none ml-auto rotate-90 lg:!rotate-0 transition-transform duration-300"
                                :class="{
                                    '!-rotate-90': activeEntry === entry,
                                }"
                            />
                        </div>

                        <!-- mobile only menu -->
                        <div v-if="entry?.children.length > 0" class="grid lg:hidden grid-rows-[0fr] overflow-hidden transition-all duration-300 -mx-4" :class="{'pt-4 grid-rows-[1fr]': activeEntry === entry}">
                            <div class="min-h-0">
                                <div
                                    v-for="(childEntry, childEntryIndex) in entry?.children"
                                    :key="childEntryIndex"
                                >
                                    <div
                                        class="text-neutral-subtle text-base font-semibold px-4 h-10 flex items-center cursor-pointer transition-all duration-150"
                                        @click.stop.prevent="toggleChildEntry(childEntry)"
                                    >
                                        <NuxtLink
                                            :to="`/${childEntry.uri}`"
                                            :target="childEntry.redirectLink?.[0].target ?? ''"
                                            class="pr-2 hover:text-neutral-extra-strong hover:underline"
                                        >
                                            {{childEntry.title}}
                                        </NuxtLink>

                                        <DButton
                                            v-if="childEntry.children?.length > 0"
                                            theme="transparent"
                                            icon="caret-down"
                                            icon-set="ph"
                                            class="ml-auto pointer-events-none transition-transform duration-300"
                                            :class="{
                                                'rotate-180': activeChildEntry === childEntry,
                                            }"
                                        />
                                    </div>

                                    <div
                                        class="grid grid-rows-[0fr] overflow-hidden transition-all duration-300"
                                        :class="{
                                            'grid-rows-[1fr]': activeChildEntry === childEntry && childEntry.children.length > 0,
                                        }"
                                    >
                                        <div class="min-h-[0px]">
                                            <NuxtLink
                                                v-for="(subChildEntry, subChildIndex) in childEntry.children"
                                                :key="subChildIndex"
                                                :to="`/${subChildEntry.uri}`"
                                                :target="subChildEntry.redirectLink?.[0].target ?? ''"
                                                class="text-neutral-base hover:underline text-sm px-4 ml-3 py-2 flex items-center cursor-pointer transition-all duration-150"
                                            >
                                                {{subChildEntry.title}}
                                            </NuxtLink>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <!-- mobile quicklinks -->
                    <div
                        v-if="quicklinks?.mainNavQuicklinksItem_Links && quicklinks.mainNavQuicklinksItem_Links.length > 0"
                        class="flex lg:hidden flex-col space-y-1 w-full p-6"
                    >
                        <NuxtLink
                            v-for="(quicklink, index) in quicklinks.mainNavQuicklinksItem_Links"
                            :key="index"
                            :to="quicklink.url"
                            class="text-neutral-strong text-sm font-medium flex items-center cursor-pointer transition-all duration-150 hover:bg-overlay-black-10 rounded-md space-x-3 p-1 lg:p-2"
                        >
                            <div v-if="quicklink?.icon?.name" class="w-9 h-9 lg:w-11 lg:h-11 rounded-md surface-brand flex items-center justify-center">
                                <DIcon
                                    :icon="quicklink.icon.name"
                                    :icon-set="quicklink.icon.set"
                                    :icon-type="quicklink.icon.type"
                                />
                            </div>
                            <span>{{quicklink.text}}</span>
                        </NuxtLink>
                    </div>
                </div>

                <div class="flex flex-1 overflow-y-auto" @mouseleave="activeChildEntry = null">
                    <div class="p-6 lg:p-0 lg:pr-9 w-full flex items-start">
                        <div class="hidden lg:block flex-1">
                            <div
                                v-for="(childEntry, childEntryIndex) in activeEntry?.children"
                                :key="childEntryIndex"
                                class="rounded-md hover:bg-overlay-black-10 hover:!opacity-100"
                                :class="{
                                    'bg-overlay-black-10': activeChildEntry === childEntry,
                                    'opacity-50': activeChildEntry && activeChildEntry !== childEntry,
                                }"
                            >
                                <div
                                    class="text-neutral-base text-lg font-semibold px-4 h-11 flex items-center cursor-pointer transition-all duration-150"
                                    @click="toggleChildEntry(childEntry)"
                                >
                                    <NuxtLink
                                        :to="`/${childEntry.uri}`"
                                        :target="childEntry.redirectLink?.[0].target ?? ''"
                                        class="pr-2 hover:text-neutral-extra-strong hover:underline"
                                        @click.stop.prevent
                                    >
                                        {{childEntry.title}}
                                    </NuxtLink>

                                    <DButton
                                        v-if="childEntry.children?.length > 0"
                                        theme="transparent"
                                        icon="caret-down"
                                        icon-set="ph"
                                        class="ml-auto pointer-events-none transition-transform duration-300"
                                        :class="{
                                            'rotate-180': activeChildEntry === childEntry,
                                        }"
                                    />
                                </div>

                                <div
                                    v-if="childEntry.children?.length > 0"
                                    class="grid grid-rows-[0fr] overflow-hidden transition-all duration-300"
                                    :class="{
                                        'grid-rows-[1fr]': activeChildEntry === childEntry && childEntry.children.length > 0,
                                    }"
                                >
                                    <div class="min-h-[0px]">
                                        <NuxtLink
                                            v-for="(subChildEntry, subChildIndex) in childEntry.children"
                                            :key="subChildIndex"
                                            :to="`/${subChildEntry.uri}`"
                                            :target="subChildEntry.redirectLink?.[0].target ?? ''"
                                            class="text-neutral-base hover:text-neutral-extra-strong hover:underline text-sm px-4 py-2 flex items-center cursor-pointer transition-all duration-150"
                                            exact-active-class="text-neutral-extra-strong font-medium"
                                        >
                                            {{subChildEntry.title}}
                                        </NuxtLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div v-if="watermark" class="hidden lg:block absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 pointer-events-none">
                <img :src="watermark.url">
            </div>
        </div>

    </div>
</template>

<script setup>
    import { NuxtLink } from '#components'
    import { useScrollLock } from '@vueuse/core'
    import { DButton, DDropdown, DDropdownItem, DIcon } from '@digistorm/spark'

    const emit = defineEmits(['close'])

    const props = defineProps({
        settings: Object,
        entries: Array,
        position: {
            type: String,
            default: 'right'
        },
        showSearch: Boolean
    })

    const { buttonTheme } = useButtonTheme()

    const route = useRoute()

    const scrollLocked = useScrollLock(window)

    const active = ref(false)
    const actionButtons = ref(null)
    const useActionDropdown = ref(false)

    const activeEntry = ref(props.entries[0])
    const activeChildEntry = ref(null)

    const rootUri = computed(() => {
        return first(route.params.uri)
    })

    const childUri = computed(() => {
        return nth(route.params.uri, 1)
    })

    const buttons = computed(() => {
        return props.settings.mainNav_Buttons ? props.settings.mainNav_Buttons.filter(button => !button.isEmpty) : []
    })

    const quicklinks = computed(() => {
        return first(props.settings.mainNav_Quicklinks)
    })

    const watermark = computed(() => {
        return first(props.settings.mainNav_Watermark)
    })

    const onMouseEnter = (event, entry) => {
        activeEntry.value = entry
    }

    const toggleChildEntry = (entry) => {
        if (activeChildEntry.value === entry) {
            activeChildEntry.value = null
        } else {
            activeChildEntry.value = entry
        }
    }

    const closeMenu = () => {
        activeChildEntry.value = null
        active.value = false
        scrollLocked.value = false

        delay(() => {
            emit('close')
        }, 500)
    }

    onMounted(() => {
        scrollLocked.value = true

        activeEntry.value = find(props.entries, { uri: rootUri.value })
        activeChildEntry.value = find(activeEntry.value?.children, { uri: `${rootUri.value}/${childUri.value}` })

        delay(() => {
            active.value = true

            // check if action buttons are overflowing
            if (actionButtons.value) {
                useActionDropdown.value = actionButtons.value.clientWidth > 360
            }
        }, 1)
    })


    onBeforeRouteLeave(() => {
        // close the nav on route change
        closeMenu()
    })

</script>

<style lang="scss" scoped>

</style>
